import { DiscountDto, PositionDto, TaxEntryDTO, TaxType } from '../DocumentT';

const taxInPercentage = (tax: number) => 1 + tax / 100;

export const roundPrice = (price: number) => Math.round(price * 100.0) / 100.0;

export const calculateNetPrice = (grossPrice: number, tax: number) => roundPrice(grossPrice / taxInPercentage(tax));

const calculateTaxes = (taxType: TaxType, netOrGrossPrice: number, taxRate: number) => {
  const vatPercentage = taxRate / 100;
  const vat =
    taxType === 'gross'
      ? netOrGrossPrice - netOrGrossPrice / (1 + vatPercentage)
      : netOrGrossPrice * vatPercentage;

  return roundPrice(vat);
};

export const getDiscountAmount = (discountType: DiscountDto['type'], discountValue: number, grossTotal: number) => {
  if (discountType === 'ABSOLUTE') {
    return discountValue;
  }

  return grossTotal * (discountValue / 100);
};

export const getDiscountPercentageValue = (
  discountType: DiscountDto['type'],
  discountValue: number,
  grossTotal: number,
) => {
  if (grossTotal === 0) {
    return 0;
  }

  if (discountType === 'ABSOLUTE') {
    return discountValue / grossTotal;
  }

  return discountValue / 100;
};

const getVatTaxTotal = (taxType: TaxType, positions: PositionDto[], discountPercentage: number): TaxEntryDTO[] => {
  if (taxType !== 'net' && taxType !== 'gross') {
    return [];
  }

  const taxTotals: Record<number, { amount: number; taxAmount: number }> = {};
  const documentPercentDiscount = 1 - discountPercentage;

  positions.forEach((position) => {
    const taxRate = position.tax ?? 0;
    if (!taxRate) {
      return;
    }

    const baseAmount = taxType === 'net' ? position.netPrice : position.price;
    const discountedAmount = roundPrice(baseAmount * documentPercentDiscount);
    const taxAmount = calculateTaxes(taxType, discountedAmount, taxRate);
    const current = taxTotals[taxRate] ?? { amount: 0, taxAmount: 0 };

    taxTotals[taxRate] = {
      amount: roundPrice(current.amount + discountedAmount),
      taxAmount: roundPrice(current.taxAmount + taxAmount),
    };
  });

  return Object.entries(taxTotals)
    .map(([taxRatePercent, values]) => ({
      taxRatePercent: Number(taxRatePercent),
      amount: values.amount,
      taxAmount: values.taxAmount,
    }))
    .sort((a, b) => a.taxRatePercent - b.taxRatePercent);
};

interface DocumentPricingSource {
  taxType: TaxType;
  discount?: DiscountDto | null;
}

export interface DocumentSummary {
  subtotal: number;
  discountAmount: number;
  total: number;
  netTotal: number;
  taxEntries: TaxEntryDTO[];
}

export const getDocumentSummary = (document: DocumentPricingSource, positions: PositionDto[]): DocumentSummary => {
  const { taxType, discount } = document;
  const { value: discountValue, type: discountType } = discount ?? { value: 0, type: 'PERCENT' as const };

  const subtotal = roundPrice(
    positions.reduce((sum, position) => sum + (taxType === 'net' ? position.netPrice : position.price), 0),
  );
  const discountPercentage = getDiscountPercentageValue(discountType, discountValue, subtotal);
  const discountAmount = roundPrice(getDiscountAmount(discountType, discountValue, subtotal));
  const sumAfterDiscount = roundPrice(subtotal - discountAmount);
  const taxEntries = getVatTaxTotal(taxType, positions, discountPercentage);
  const totalTax = roundPrice(taxEntries.reduce((sum, taxEntry) => sum + taxEntry.taxAmount, 0));

  if (taxType === 'net') {
    return {
      subtotal,
      discountAmount,
      total: roundPrice(sumAfterDiscount + totalTax),
      netTotal: sumAfterDiscount,
      taxEntries,
    };
  }

  if (taxType === 'gross') {
    return {
      subtotal,
      discountAmount,
      total: sumAfterDiscount,
      netTotal: roundPrice(sumAfterDiscount - totalTax),
      taxEntries,
    };
  }

  return {
    subtotal,
    discountAmount,
    total: sumAfterDiscount,
    netTotal: sumAfterDiscount,
    taxEntries: [],
  };
};
